import { readStorage, removeStorage, writeStorage } from '@/utils/storage/base'

export const storageKeys = {
  locale: 'locale',
  marketingLang: 'lang',
  theme: 'theme',
  marketingCookieConsent: 'easyhire_marketing_cookie_consent',
  postLoginRedirect: 'easyhire_post_login_redirect',
  userToken: 'token',
  userProfile: 'user',
  avatarPreferencePrefix: 'easyhire_avatar_preference',
  adminMode: 'easyhire_admin_mode',
  extensionDebug: 'easyhire_debug_extension_sync',
  announcementReadPrefix: 'easyhire_announcement_read',
  referralTicket: 'easyhire_referral_ticket',
} as const

export type MarketingCookieConsent = 'accepted' | 'declined'

export type StoredReferralTicket = {
  code: string
  ticket: string
  expiresAt: string
}

const getSessionStorage = () => {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}

const getAnnouncementReadKey = (userId: string, announcementId: string) =>
  `${storageKeys.announcementReadPrefix}:${userId}:${announcementId}`
const getAvatarPreferenceKey = (userId: string) => `${storageKeys.avatarPreferencePrefix}:${userId}`

const supportedLocales = new Set(['zh-CN', 'en'])

const normalizeLocale = (value: string | null | undefined) => {
  const normalized = String(value || '').trim()
  const lower = normalized.toLowerCase()
  if (supportedLocales.has(normalized)) return normalized
  if (lower.startsWith('zh')) return 'zh-CN'
  if (lower.startsWith('en')) return 'en'
  return ''
}

const getBrowserLocale = () => {
  if (typeof window === 'undefined' || typeof window.navigator === 'undefined') return 'en'

  const languages = Array.isArray(window.navigator.languages) ? [...window.navigator.languages] : []
  const candidates = [...languages, window.navigator.language]
  return candidates.some((item) => String(item || '').toLowerCase().startsWith('zh'))
    ? 'zh-CN'
    : 'en'
}

const getDefaultLocale = () =>
  normalizeLocale(readStorage(storageKeys.locale, null)) || getBrowserLocale()

export const appStorage = {
  getLocale: getDefaultLocale,
  setLocale: (value: string) => writeStorage(storageKeys.locale, value),
  getTheme: () => (readStorage(storageKeys.theme, 'light') || 'light') as 'light' | 'dark',
  setTheme: (value: 'light' | 'dark') => writeStorage(storageKeys.theme, value),
  getMarketingLang: () => (readStorage(storageKeys.marketingLang, 'en') || 'en') as 'en' | 'zh',
  setMarketingLang: (value: 'en' | 'zh') => writeStorage(storageKeys.marketingLang, value),
  getMarketingCookieConsent: () => {
    const consent = readStorage(storageKeys.marketingCookieConsent, null)
    return consent === 'accepted' || consent === 'declined' ? consent : null
  },
  setMarketingCookieConsent: (value: MarketingCookieConsent) =>
    writeStorage(storageKeys.marketingCookieConsent, value),
  getPostLoginRedirect: () => readStorage(storageKeys.postLoginRedirect, null, getSessionStorage()),
  setPostLoginRedirect: (value: string) =>
    writeStorage(storageKeys.postLoginRedirect, value, getSessionStorage()),
  clearPostLoginRedirect: () => removeStorage(storageKeys.postLoginRedirect, getSessionStorage()),
  getReferralTicket: (): StoredReferralTicket | null => {
    const raw = readStorage(storageKeys.referralTicket, null, getSessionStorage())
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as Partial<StoredReferralTicket>
      if (!parsed.code || !parsed.ticket || !parsed.expiresAt) return null
      return {
        code: String(parsed.code),
        ticket: String(parsed.ticket),
        expiresAt: String(parsed.expiresAt),
      }
    } catch {
      return null
    }
  },
  setReferralTicket: (value: StoredReferralTicket) =>
    writeStorage(storageKeys.referralTicket, JSON.stringify(value), getSessionStorage()),
  clearReferralTicket: () => removeStorage(storageKeys.referralTicket, getSessionStorage()),
  getUserToken: () => readStorage(storageKeys.userToken, null),
  setUserToken: (value: string) => writeStorage(storageKeys.userToken, value),
  clearUserToken: () => removeStorage(storageKeys.userToken),
  getUserProfileRaw: () => readStorage(storageKeys.userProfile, null),
  setUserProfileRaw: (value: string) => writeStorage(storageKeys.userProfile, value),
  clearUserProfile: () => removeStorage(storageKeys.userProfile),
  getAvatarPreference: (userId: string) => readStorage(getAvatarPreferenceKey(userId), null),
  setAvatarPreference: (userId: string, value: string) =>
    writeStorage(getAvatarPreferenceKey(userId), value),
  clearAvatarPreference: (userId: string) => removeStorage(getAvatarPreferenceKey(userId)),
  isAnnouncementRead: (userId: string, announcementId: string) =>
    readStorage(getAnnouncementReadKey(userId, announcementId), '0') === '1',
  setAnnouncementRead: (userId: string, announcementId: string, read = true) =>
    read
      ? writeStorage(getAnnouncementReadKey(userId, announcementId), '1')
      : removeStorage(getAnnouncementReadKey(userId, announcementId)),
  isAdminModeEnabled: () => readStorage(storageKeys.adminMode, '0') === '1',
  setAdminMode: (enabled: boolean) =>
    enabled ? writeStorage(storageKeys.adminMode, '1') : removeStorage(storageKeys.adminMode),
  isExtensionDebugEnabled: () => readStorage(storageKeys.extensionDebug, '0') === '1',
}
